<template>
  <div class="relative">
    <input
      ref="qrInput"
      :key="inputKey"
      :value="modelValue"
      @input="handleInput"
      @keydown="handleKeydown"
      @keyup="handleKeyup"
      @paste="handlePaste"
      @focus="isInputFocused = true; $emit('focus')"
      @blur="isInputFocused = false; $emit('blur')"
      @change="handleChange"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="inputClasses"
      type="text"
      autocomplete="off"
      autocorrect="off"
      autocapitalize="off"
      spellcheck="false"
    />
  </div>
</template>

<script>
export default {
  name: 'QRTextInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Введите или отсканируйте QR-код'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inputClass: {
      type: String,
      default: ''
    },
    autoClear: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'qr-scanned', 'input-complete', 'scan-complete', 'focus', 'blur'],
  data() {
    return {
      isInputFocused: false,
      lastInputTime: 0,
      scanDetectionTimer: null,
      // Убираем lastEmittedValue для поддержки повторного сканирования
      // Словарь для перевода с русской раскладки
      translationMap: {
        'й': 'q', 'ц': 'w', 'у': 'e', 'к': 'r', 'е': 't',
        'н': 'y', 'г': 'u', 'ш': 'i', 'щ': 'o', 'з': 'p',
        'х': '[', 'ъ': ']', 'ф': 'a', 'ы': 's', 'в': 'd',
        'а': 'f', 'п': 'g', 'р': 'h', 'о': 'j', 'л': 'k',
        'д': 'l', 'ж': ';', 'э': '\'', 'я': 'z', 'ч': 'x',
        'с': 'c', 'м': 'v', 'и': 'b', 'т': 'n', 'ь': 'm',
        'б': ',', 'ю': '.', '?': '&',
        'Й': 'Q', 'Ц': 'W', 'У': 'E', 'К': 'R', 'Е': 'T',
        'Н': 'Y', 'Г': 'U', 'Ш': 'I', 'Щ': 'O', 'З': 'P',
        'Х': '{', 'Ъ': '}', 'Ф': 'A', 'Ы': 'S', 'В': 'D',
        'А': 'F', 'П': 'G', 'Р': 'H', 'О': 'J', 'Л': 'K',
        'Д': 'L', 'Ж': ':', 'Э': '"', 'Я': 'Z', 'Ч': 'X',
        'С': 'C', 'М': 'V', 'И': 'B', 'Т': 'N', 'Ь': 'M',
        'Б': '<', 'Ю': '>', '?': '&'
      },
      // Добавляем флаг для отслеживания процесса сброса
      isResetting: false,
      inputKey: 0, // Добавляем ключ для принудительного пересоздания input
      // Отслеживание дублированных сканирований
      lastScanData: '',
      lastScanTime: 0,
      scanHistory: [], // История последних 5 сканирований
      // Флаг для предотвращения двойного вызова emitScanComplete (таймер + Enter)
      scanCompleteEmitted: false,
      scanCompleteResetTimer: null
    };
  },
  computed: {
    inputClasses() {
      const baseClasses = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
      return this.inputClass ? `${baseClasses} ${this.inputClass}` : baseClasses;
    }
  },
  beforeUnmount() {
    // Очищаем таймеры при размонтировании
    if (this.scanDetectionTimer) {
      clearTimeout(this.scanDetectionTimer);
    }
    if (this.scanCompleteResetTimer) {
      clearTimeout(this.scanCompleteResetTimer);
    }
  },
  methods: {
    handleInput(event) {
      const value = event.target.value;
      const currentTime = Date.now();
      
      // Переводим русские символы в английские
      const translatedValue = this.translateRussianToEnglish(value);
      
      // Обновляем модель переведенным значением
      this.$emit('update:modelValue', translatedValue);
      
      // Эмитим событие ввода для каждого изменения
      this.$emit('input-complete', translatedValue);
      
      // Определяем сканирование по скорости ввода
      const timeDiff = currentTime - this.lastInputTime;
      this.lastInputTime = currentTime;
      
      // Очищаем предыдущий таймер
      if (this.scanDetectionTimer) {
        clearTimeout(this.scanDetectionTimer);
      }
      
      // Если ввод происходит быстро (менее 50ms между символами) - возможно сканер
      if (timeDiff < 50 && translatedValue.length > 3) {
        // Ждем паузу в вводе для определения завершения сканирования
        this.scanDetectionTimer = setTimeout(() => {
          this.emitScanComplete(translatedValue);
        }, 100);
      }
    },

    handleKeydown(event) {
      // Обрабатываем завершающие клавиши сканера
      const completionKeys = ['Enter', 'Tab', 'Return'];
      const completionCodes = [13, 9]; // Enter, Tab
      
      if (completionKeys.includes(event.key) || completionCodes.includes(event.keyCode)) {
        event.preventDefault();
        
        // Очищаем таймер, так как получили завершающую клавишу
        if (this.scanDetectionTimer) {
          clearTimeout(this.scanDetectionTimer);
          this.scanDetectionTimer = null;
        }
        
        // Пропускаем, если emitScanComplete уже вызван по таймеру (предотвращение дубликата)
        if (this.scanCompleteEmitted) {
          return;
        }
        
        if (this.modelValue && this.modelValue.length > 0) {
          this.emitScanComplete(this.modelValue);
        }
      }
    },

    handleKeyup(event) {
      // keyup только preventDefault — вызов emitScanComplete оставлен в handleKeydown
      const completionKeys = ['Enter', 'Tab'];
      const completionCodes = [13, 9];
      if (completionKeys.includes(event.key) || completionCodes.includes(event.keyCode)) {
        event.preventDefault();
      }
    },

    handleChange(event) {
      // Обработка события change для Android ТСД
      const value = event.target.value;
      if (value && value.length > 3) {
        // Дополнительная проверка на завершение сканирования через change
        setTimeout(() => {
          if (this.modelValue === value) {
            this.emitScanComplete(value);
          }
        }, 50);
      }
    },

    handlePaste(event) {
      // Обработка вставки QR-кода
      const pastedText = event.clipboardData.getData('text');
      if (pastedText) {
        // Эмитим событие завершения сканирования для вставки
        this.emitScanComplete(pastedText);
      }
    },

    decodeUnicodeEscapes(str) {
      // Заменяем \u0000 последовательности на соответствующие символы
      return str.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
      });
    },

    processScanData(data) {
      if (!data || data.trim() === '') {
        return;
      }

      // Убираем служебные символы в конце (Enter, пробелы и т.д.)
      let cleanData = data.replace(/Enter$|[\r\n\s]+$/g, '');
      
      // Декодируем unicode-escape последовательности (\u0413 -> Г)
      cleanData = this.decodeUnicodeEscapes(cleanData);

      // Проверяем является ли это JSON (для QR-кодов)
      try {
        const parsedData = JSON.parse(cleanData);
        // Эмитим событие сканирования QR-кода
        this.$emit('qr-scanned', parsedData);
      } catch (error) {
        // Если не JSON, то это обычный текст - ничего не делаем
        // Значение уже обновлено в handleInput
      }
    },

    translateRussianToEnglish(text) {
      // Переводим каждый символ используя translationMap
      return text.split('').map(char => this.translationMap[char] || char).join('');
    },

    emitScanComplete(value) {
      // Проверка на дублированные сканирования
      const currentTime = Date.now();
      const isDuplicate = this.isDuplicateScan(value, currentTime);
      
      if (isDuplicate) {
        // При autoClear=false не очищаем поле (страница контроля серийных номеров)
        if (this.autoClear) {
          this.forceRecreateInput();
        }
        return;
      }
      
      // Сохраняем данные сканирования в историю
      this.addToScanHistory(value, currentTime);
      
      this.$emit('scan-complete', value);
      this.processScanData(value);
      
      // Флаг для предотвращения повторного вызова из handleKeydown (таймер уже сработал)
      this.scanCompleteEmitted = true;
      if (this.scanCompleteResetTimer) {
        clearTimeout(this.scanCompleteResetTimer);
      }
      this.scanCompleteResetTimer = setTimeout(() => {
        this.scanCompleteEmitted = false;
        this.scanCompleteResetTimer = null;
      }, 600);
      
      // ИСПРАВЛЕНИЕ: учитываем параметр autoClear
      if (this.autoClear) {
        this.performHardReset();
      } else {
        // Если автоочистка отключена, делаем только легкий сброс состояния
        this.lastInputTime = 0;
        if (this.scanDetectionTimer) {
          clearTimeout(this.scanDetectionTimer);
          this.scanDetectionTimer = null;
        }
      }
    },

    // Проверка на дублированное сканирование
    isDuplicateScan(value, currentTime) {
      // ИСПРАВЛЕНИЕ: уменьшаем время блокировки до 500ms (0.5 секунды)
      // Это позволит быстро сканировать разные ячейки, но предотвратит случайные дубликаты
      if (this.lastScanData === value && (currentTime - this.lastScanTime) < 500) {
        return true;
      }
      
      // УБИРАЕМ проверку истории - она слишком агрессивная
      // Оставляем только проверку на быстрые повторы одного и того же значения
      
      return false;
    },

    // Добавление сканирования в историю
    addToScanHistory(value, currentTime) {
      this.lastScanData = value;
      this.lastScanTime = currentTime;
      
      // Добавляем в историю
      this.scanHistory.push({
        data: value,
        time: currentTime
      });
      
      // Оставляем только последние 10 записей
      if (this.scanHistory.length > 10) {
        this.scanHistory = this.scanHistory.slice(-10);
      }
    },

    // Новый метод для жесткого сброса состояния (особенно для Android ТСД)
    performHardReset() {
      if (this.isResetting) return; // Предотвращаем множественные вызовы
      
      this.isResetting = true;
      
      // 1. Сбрасываем внутренние состояния компонента
      this.lastInputTime = 0;
      this.isInputFocused = false;
      
      // 2. Очищаем историю сканирований
      this.clearScanHistory();
      
      // 3. Очищаем все таймеры
      if (this.scanDetectionTimer) {
        clearTimeout(this.scanDetectionTimer);
        this.scanDetectionTimer = null;
      }
      
      // 4. Эмитим обновление модели
      this.$emit('update:modelValue', '');
      
      // 5. РАДИКАЛЬНЫЙ ПОДХОД: пересоздаем input элемент
      this.forceRecreateInput();
      
      // 6. Дополнительный сброс через nextTick для гарантии
      this.$nextTick(() => {
        setTimeout(() => {
          const inputElement = this.$refs.qrInput;
          if (inputElement && !this.isResetting) {
            // Финальная проверка и очистка
            if (inputElement.value !== '') {
              this.forceInputClear();
            }
            this.isResetting = false;
          }
        }, 300); // Увеличиваем задержку для Android
      });
    },

    // Метод для принудительного пересоздания input элемента
    forceRecreateInput() {
      // Увеличиваем ключ для принудительного пересоздания элемента
      this.inputKey++;
      
      // Дополнительная очистка через DOM после пересоздания
      this.$nextTick(() => {
        const inputElement = this.$refs.qrInput;
        if (inputElement) {
          this.forceInputClear();
        }
      });
    },

    // Метод для принудительной очистки кеша браузера (для Android ТСД)
    forceInputClear() {
      const inputElement = this.$refs.qrInput;
      if (inputElement) {
        // Принудительно убираем фокус
        inputElement.blur();
        
        // Множественная очистка для обхода кеширования Android
        inputElement.value = '';
        inputElement.defaultValue = '';
        inputElement.setAttribute('value', '');
        inputElement.removeAttribute('value');
        
        // Очищаем дополнительные атрибуты которые могут кешироваться
        inputElement.removeAttribute('data-value');
        inputElement.removeAttribute('data-cached');
        
        // Принудительное создание нового input события
        ['input', 'change', 'keyup', 'keydown', 'focus', 'blur'].forEach(eventType => {
          const event = new Event(eventType, { bubbles: true, cancelable: true });
          inputElement.dispatchEvent(event);
        });
        
        // Очистка через requestAnimationFrame для синхронизации с отрисовкой
        requestAnimationFrame(() => {
          if (inputElement) {
            inputElement.value = '';
            // Дополнительная очистка кешей браузера
            this.clearBrowserCache(inputElement);
            inputElement.focus();
          }
        });
      }
    },

    // Метод для очистки всех возможных кешей браузера
    clearBrowserCache(inputElement) {
      try {
        // Очистка истории автозаполнения
        if (inputElement.form) {
          inputElement.form.reset();
        }
        
        // Принудительная очистка внутреннего состояния браузера
        inputElement.autocomplete = 'new-password'; // Трюк для сброса автозаполнения
        setTimeout(() => {
          inputElement.autocomplete = 'off';
        }, 10);
        
        // Очистка selection для Android
        if (inputElement.setSelectionRange) {
          inputElement.setSelectionRange(0, 0);
        }
        
        // Принудительное обновление стилей для перерисовки
        const originalDisplay = inputElement.style.display;
        inputElement.style.display = 'none';
        inputElement.offsetHeight; // Принудительный reflow
        inputElement.style.display = originalDisplay;
        
      } catch (error) {
        console.warn('Ошибка при очистке кешей браузера:', error);
      }
    },

    focus() {
      if (!this.isResetting) {
        this.$refs.qrInput.focus();
      }
    },

    // Принудительный фокус, игнорирующий isResetting
    forceFocus() {
      this.$refs.qrInput.focus();
    },

    blur() {
      this.$refs.qrInput.blur();
    },

    clear() {
      // Очищаем историю сканирований
      this.clearScanHistory();
      // Используем новый метод жесткого сброса
      this.performHardReset();
    },

    resetComponent() {
      // Полный сброс компонента (для использования из родительских компонентов)
      this.clearScanHistory();
      this.performHardReset();
    },

    // Метод для очистки истории сканирований
    clearScanHistory() {
      this.lastScanData = '';
      this.lastScanTime = 0;
      this.scanHistory = [];
    }
  }
};
</script>

<style scoped>
/* Дополнительные стили при необходимости */
</style> 